import { useEffect, useMemo, useRef, useState } from "react";
import useVisitorIdentity from "./chat/useVisitorIdentity";
import useChatSession from "./chat/useChatSession";
import VisitorBanner from "./chat/VisitorBanner";
import HandoffSheet from "./chat/HandoffSheet";
import ChatLauncher from "./chat/ChatLauncher";
import ChatHeader from "./chat/ChatHeader";
import MessageList from "./chat/MessageList";
import QuickPrompts from "./chat/QuickPrompts";
import HandoffCta from "./chat/HandoffCta";
import ChatInput from "./chat/ChatInput";
import ChatFooter from "./chat/ChatFooter";

const HANDOFF_PROMINENT_THRESHOLD = 3;

export default function Chatbot() {
    const [open, setOpen] = useState(false);
    const [input, setInput] = useState("");
    const [handoffOpen, setHandoffOpen] = useState(false);
    const scrollRef = useRef(null);

    const { userName, userRole, learnFromMessage, updateIdentity, forget } =
        useVisitorIdentity();

    const { messages, sessionId, loading, send, reset, rebuildGreetingWith, appendSystem } =
        useChatSession({ initialName: userName, learn: learnFromMessage });

    const userMessageCount = useMemo(
        () => messages.filter((m) => m.role === "user").length,
        [messages]
    );

    // Swap the opening greeting once on mount if we already know the visitor's name.
    useEffect(() => {
        if (userName) rebuildGreetingWith(userName);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Auto-scroll on new content
    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
    }, [messages, open, loading]);

    const submit = (text) => {
        if (loading) return;
        const msg = (text ?? input).trim();
        if (!msg) return;
        setInput("");
        send(msg);
    };

    const onHandoffSubmitted = ({ name }) => {
        setHandoffOpen(false);
        appendSystem(
            `Thanks${name ? `, ${name}` : ""} — I just passed your message along to Dorothy and Valerie. They'll be in touch soon, usually within one business day. Is there anything else I can help you find in the meantime?`
        );
    };

    if (!open) return <ChatLauncher onOpen={() => setOpen(true)} />;

    return (
        <div
            className="fixed bottom-6 right-6 z-50 w-[calc(100vw-3rem)] sm:w-[380px]"
            data-testid="chatbot-window"
        >
            <div className="relative flex flex-col max-h-[80vh] sm:max-h-[600px] rounded-3xl bg-white border border-[#E5DFD3] shadow-2xl shadow-[#1A2230]/15 overflow-hidden">
                <ChatHeader
                    canReset={messages.length > 1 || !!sessionId}
                    onReset={() => reset(userName)}
                    onClose={() => setOpen(false)}
                />

                {userName && (
                    <VisitorBanner
                        name={userName}
                        role={userRole}
                        onUpdate={updateIdentity}
                        onForget={forget}
                    />
                )}

                <MessageList ref={scrollRef} messages={messages} loading={loading} />

                {messages.length <= 1 && <QuickPrompts onPick={submit} />}

                {userMessageCount >= 1 && (
                    <HandoffCta
                        prominent={userMessageCount >= HANDOFF_PROMINENT_THRESHOLD}
                        onClick={() => setHandoffOpen(true)}
                    />
                )}

                <ChatInput
                    value={input}
                    onChange={setInput}
                    onSubmit={submit}
                    loading={loading}
                    placeholderName={userName}
                />

                <ChatFooter />

                <HandoffSheet
                    open={handoffOpen}
                    onClose={() => setHandoffOpen(false)}
                    defaultName={userName}
                    onSubmitted={onHandoffSubmitted}
                />
            </div>
        </div>
    );
}
